import Component from '@glimmer/component'
import { ContentTile } from 'teacher-ui-client/components/tiles/content'
import { HistoryReport } from 'teacher-ui-client/components/reporting/writing-legends/quizzes-completed/history/report'
import type ReportingService from 'teacher-ui-client/services/reporting'
import { service } from '@ember/service'
import { Product } from 'teacher-ui-client/utils/products'
import { trackedFunction } from 'reactiveweb/function'
import { toRequestScope, type BaseReport, type BaseReportMetaPaging } from 'teacher-ui-client/services/reporting'
import type { UIScope } from 'teacher-ui-client/reporting/ui-scope/route'
import type { NormalizedInterval } from 'date-fns'
import { keepLatest } from 'reactiveweb/keep-latest'
import { use } from 'ember-resources'

export interface ReportData extends BaseReport {
  students: {
    avg_score: Nullable<number>
    first_name: string
    grade_position: number
    id: string
    last_name: string
    quizzes_taken: number
    school_class_names: string[]
    teacher_names: string[]
    total_attempts: number
  }[]
  meta: BaseReportMetaPaging
}

interface ReportLoaderSignature {
  Args: {
    uiScope: UIScope
    period: NormalizedInterval
    sort: string
    pageNumber: number
    perPage: number
  }
}

export class ReportLoader extends Component<ReportLoaderSignature> {
  @service reporting!: ReportingService

  fetchReport = trackedFunction(this, async () => {
    const requestScope = toRequestScope(this.args.uiScope)
    const url = this.reporting.buildGravityUrl({
      product: Product.WL,
      requestScope,
      reportName: 'quizzes-completed/quiz-results/history-table',
    })
    const query = this.reporting.buildQuery({
      sort: this.args.sort,
      page: {
        number: this.args.pageNumber,
        size: this.args.perPage,
      },
      period: this.args.period,
    })
    const report = await this.reporting.fetchReport<ReportData>(url, query)
    return report.students ? report : undefined
  })

  @use
  latestReport = keepLatest({
    value: () => this.fetchReport.value,
    when: () => this.fetchReport.isPending,
  }); /* prettier-ignore */ /* prettier-ignore-end */

  <template>
    {{#if this.latestReport}}
      <ContentTile>
        <HistoryReport
          @isReloading={{this.fetchReport.isPending}}
          @reportData={{this.latestReport}}
          @scopeName={{@uiScope.scope}}
        />
      </ContentTile>
    {{/if}}
  </template>
}
